/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import { useTheme } from "@/components/theme-provider";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Themes } from "@/components/ui/themes";
import { toast } from "@/components/ui/use-toast";
import {
  useCustomTheme,
  useLeftbar,
  usePercentageStore,
} from "@/dashboard/store/store";
import {
  PROJECT_QUERY_GC_TIME,
  PROJECT_QUERY_STALE_TIMES,
  projectQueryKeys,
} from "@/dashboard/projectQueryConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import { useArrow } from "@/hooks/useArrow";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { defaultThemes } from "@/libs/themesList";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import HeaderTop from "./HeaderTop";
import Leftbar from "./Leftbar";
import ProjectCreationChoiceDialog from "@/dashboard/components/project/ProjectCreationChoiceDialog";
import ProjectCustomTypeDialog from "@/dashboard/components/project/ProjectCustomTypeDialog";
type ThemeColors = Record<string, string>;

type ThemeVars = {
  name: string;
  value: string;
  colors: ThemeColors;
};
const getLocalTheme = (): ThemeVars | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem("userTheme");
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};
const LayoutDashboard: FC<{ children: ReactNode; showButtons?: boolean }> = ({
  children,
  showButtons,
}) => {
  const { isOpen, setIsOpen } = useLeftbar();
  const { enablePreviewLine, percentage, resetPercentage } =
    usePercentageStore();
  const { isVisible, setIsVisible } = useCustomTheme();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const projectRouteMatch = location.pathname.match(/^\/dashboard\/project\/(\d+)/);
  const projectIdForSummary = projectRouteMatch?.[1] ?? null;
  useFetchDataV2<any>(
    projectIdForSummary
      ? `${import.meta.env.VITE_API_ENDPOINT}/project/${projectIdForSummary}/summary`
      : "",
    {
      cacheId: projectIdForSummary
        ? projectQueryKeys.summary(projectIdForSummary)
        : "project-summary",
      staleTime: PROJECT_QUERY_STALE_TIMES.summary,
      gcTime: PROJECT_QUERY_GC_TIME,
      browserCache: true,
      browserCacheNamespace: "project-summary",
      browserCacheTtlMs: 1000 * 60 * 60,
    },
  );
  const { postData } = usePostData<any>();
  const [loadingSaveTheme, setLoadingSaveTheme] = useState(false);
  const [customColors, setCustomColors] = useState<ThemeVars>(() => {
    const localTheme = getLocalTheme();
    if (localTheme) return localTheme;
    // fallback thème clair par défaut
    const defaultTheme = defaultThemes.find((t) => t.value === "light");
    return defaultTheme || { name: "", value: "", colors: {} };
  });

  const { user } = useAuth();
  const saveCustomThemeToDatabase = async (colors: ThemeVars | string) => {
    try {
      setLoadingSaveTheme(true);
      const res = await postData({
        url: `${import.meta.env.VITE_IP_API}/api/theme`,
        data: {
          theme: JSON.stringify(colors),
        },
      });
      if (res === null) {
        setLoadingSaveTheme(false);
        return;
      }
      localStorage.setItem("userTheme", JSON.stringify(colors));
      toast({
        title: "Votre theme a bien été modifié.",
        duration: 1000,
      });

      // Force un rafraîchissement de la page
      window.location.reload();
      setLoadingSaveTheme(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du thème :", error);
    }
  };

  useEffect(() => {
    if (user?.theme && user?.theme !== "") {
      const parsed = JSON.parse(user.theme);
      const localTheme = getLocalTheme();
      // comparer pour éviter écrasement inutile et flicker
      if (JSON.stringify(parsed) !== JSON.stringify(localTheme)) {
        setCustomColors(parsed);
        localStorage.setItem("userTheme", user.theme);
      }
    }
  }, [user]);

  useEffect(() => {
    // Appliquer les couleurs personnalisées à chaque changement
    for (const key in customColors.colors) {
      document.documentElement.style.setProperty(key, customColors.colors[key]);
    }
  }, [customColors]);
  const { canvasRef } = useArrow();
  const handleResetTheme = () => {
    const currentThemeData = defaultThemes.find((t) => t.value === "light");
    if (currentThemeData) {
      setCustomColors(currentThemeData);
      localStorage.setItem("userTheme", currentThemeData);
    }
    saveCustomThemeToDatabase(currentThemeData as ThemeVars);
    setIsEditing(true);
    // // Force un rafraîchissement de la page
    // window.location.reload();
  };
  useEffect(() => {
    if (percentage == 100) resetPercentage();
  }, [percentage]);
  if (loadingSaveTheme) return "Chargement...";
  return (
    <div id="container-main" className="relative">
      <ResizablePanelGroup
        className="relative flex max-h-screen flex-wrap"
        direction={"horizontal"}
      >
        <ResizablePanel
          className="max-h-screen overflow-y-auto relative  z[1000]"
          maxSize={isOpen ? 30 : 3}
          minSize={isOpen ? 16 : 3}
          defaultSize={isOpen ? 16 : 3}
        >
          <Leftbar handleToggleLeftbar={setIsOpen} isOpen={isOpen} />
          <div className="absolute top-0 left-0  w-full h-full bg-foreground opacity-5 -z-10"></div>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <button
            type="button"
            aria-label={isOpen ? "Replier la barre latérale" : "Déplier la barre latérale"}
            title={isOpen ? "Replier la barre latérale" : "Déplier la barre latérale"}
            className="fixed bottom-5 z-[2000] pointer-events-auto flex size-8 items-center justify-center rounded-md rounded-s-none border border-s-0 bg-gray-100 text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <PanelLeftClose size={18} className="text-muted-foreground" />
            ) : (
              <PanelLeftOpen size={18} className="text-muted-foreground" />
            )}
          </button>
          <ScrollArea
            className={`max-h-screen overflow-x-auto ${
              showButtons ? "mt-5" : ""
            }`}
          >
            <div className="flex-1 pl-10 pr-12 ">
              <HeaderTop showButtons={showButtons} />
            </div>
            <div
              key={`${location.pathname}${location.search}`}
              className="mx-8 "
            >
              {children}
            </div>
          </ScrollArea>
          {enablePreviewLine && (
            <div
              className="fixed left-0 top-0 z-[999999] h-1 w-full bg-green-600"
              style={{ width: percentage + "%" }}
            ></div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
      {isVisible && (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-0 w-[90%] md:w-full md:h-16  flex justify-center items-center z-50">
          <Themes
            customColors={customColors}
            setCustomColors={setCustomColors}
            isOpen={open}
            setIsOpen={setOpen}
            handleResetTheme={handleResetTheme}
            saveCustomThemeToDatabase={saveCustomThemeToDatabase}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            setVisible={setIsVisible}
          />
        </div>
      )}
      <ProjectCreationChoiceDialog />
      <ProjectCustomTypeDialog />
    </div>
  );
};

export default LayoutDashboard;
